import { useCallback } from "react";
import { atom, useRecoilValue, useSetRecoilState } from "recoil";

import { setAuthInfoHeader } from "../../services/granularAxios";
import { SimpleSetValue } from "../../types";

export type AuthToken = string;

// Do NOT export atoms.
// This allows us to limit usage of these to only the hooks we export below.

const authTokenAtom = atom<AuthToken | undefined>({
  key: "[@fabric/core] authTokenAtom",
  default: undefined,
});

export const authTokenAccessError = new Error(
  "Attempted to access authToken but it is not available",
);

// #####################
// ## useSetAuthToken ##
// #####################

export type ResultOfUseSetAuthToken = SimpleSetValue<AuthToken | undefined>;
export type UseSetAuthToken = () => ResultOfUseSetAuthToken;

export const useSetAuthToken: UseSetAuthToken = () => {
  const setAuthToken = useSetRecoilState(authTokenAtom);
  return useCallback(
    (authToken: AuthToken | undefined) => {
      setAuthInfoHeader({ identityToken: authToken });
      setAuthToken(authToken);
    },
    [setAuthToken],
  );
};

// #######################
// ## useMaybeAuthToken ##
// #######################

export type ResultOfUseMaybeAuthToken = AuthToken | undefined;
export type UseMaybeAuthToken = () => ResultOfUseMaybeAuthToken;

export const useMaybeAuthToken: UseMaybeAuthToken = () => {
  return useRecoilValue(authTokenAtom);
};

// ##################
// ## useAuthToken ##
// ##################

export type ResultOfUseAuthToken = AuthToken;
export type UseAuthToken = () => ResultOfUseAuthToken;

export const useAuthToken: UseAuthToken = () => {
  const authToken = useMaybeAuthToken();
  if (authToken === undefined) {
    throw authTokenAccessError;
  }
  return authToken;
};
